import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type { EntitledFramework, RetainedCounts } from '@/api/types/cts-frameworks.types';

// ── TC3 — the tenant Frameworks workspace ────────────────────────────────────────────────────

export function useEntitledFrameworks() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-frameworks', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/workspace/frameworks');
      return data.data.frameworks as EntitledFramework[];
    },
    enabled: !!tenantId,
  });
}

/**
 * ⚠ BOTH MUTATIONS INVALIDATE THE CONTROLS WORKSPACE TOO. Adoption is the SCOPE of that view, so
 * adding or removing a framework changes which controls are in it. Leaving the controls list on a
 * stale cache would show the tenant a workspace that no longer matches the frameworks they just
 * changed — and it would look correct, because nothing about it is visibly wrong.
 */
function useAdoptionMutation(verb: 'adopt' | 'remove') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reference: string) => {
      const { data } = await apiClient.post(`/compliance/workspace/frameworks/${reference}/${verb}`);
      return data.data as { reference: string; adopted: boolean; retained?: RetainedCounts };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-frameworks'] });
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
    },
  });
}

export const useAddFramework = () => useAdoptionMutation('adopt');
export const useRemoveFramework = () => useAdoptionMutation('remove');
